import { DashboardQuoteItem } from "@/fm/hooks/use_dashboard_quote_query";
import {
  ACCESS_ADD_ON_TYPE,
  AddonPricingType,
  QuoteItemOrderableType,
  SpaceOffering,
  isPerBookingPricing,
  isWholeSpacePricing,
} from "@/offering/api";
import { OrderItem, OrderItemMultiPass, OrderableType } from "@/fm/data/orders";
import { AddOnSelections } from "@/fm/utils/amendment_quote_utils";

const DEFAULT_ADD_ON_LABEL = "Add-on";

function isAccessAddOnItem(item: OrderItem): item is OrderItemMultiPass {
  return (
    item.type === OrderableType.AddOn &&
    item.details?.addOnType === ACCESS_ADD_ON_TYPE
  );
}

function isEditableAddOnItem(item: OrderItem): item is OrderItemMultiPass {
  return (
    item.type === OrderableType.AddOn &&
    item.details?.addOnType !== ACCESS_ADD_ON_TYPE
  );
}

interface AddOnsBreakdownLine {
  id: string;
  label: string;
  amount: number;
}

export interface AddOnsBreakdown {
  label: string;
  amount: number;
  lines: AddOnsBreakdownLine[];
}

interface GroupedAddOn {
  id: string;
  name: string;
  amount: number;
  quantityPerDay: number;
  daysCount: number;
  pricingType?: AddonPricingType;
}

// Always the compact count form ("x 2 x 4 days"), never a list of the days
// themselves — which days a partial selection covers is answered by the add-on
// row and the pre-save confirmation, not by the price breakdown.
function formatAddOnLabel({
  name,
  quantityPerDay,
  daysCount,
  pricingType,
}: {
  name: string;
  quantityPerDay: number;
  daysCount: number;
  pricingType?: AddonPricingType;
}): string {
  // Per-booking pricing bills once for the whole booking, so it has no day
  // dimension to report.
  if (isPerBookingPricing(pricingType)) {
    return pricingType === AddonPricingType.QuantityPerBooking
      ? `${name} x ${quantityPerDay}`
      : name;
  }
  if (isWholeSpacePricing(pricingType)) {
    return daysCount > 1 ? `${name} x ${daysCount} days` : name;
  }
  return daysCount > 1
    ? `${name} x ${quantityPerDay} x ${daysCount} days`
    : `${name} x ${quantityPerDay}`;
}

function buildBreakdown(
  lines: AddOnsBreakdownLine[]
): AddOnsBreakdown | undefined {
  if (lines.length === 0) {
    return undefined;
  }
  return {
    label: `Add-ons (${lines.length})`,
    amount: lines.reduce((total, line) => total + line.amount, 0),
    lines,
  };
}

function groupByAddOn<T>(
  items: T[],
  getId: (item: T) => string,
  getFields: (item: T) => {
    name: string;
    amount: number;
    quantity: number;
    pricingType?: AddonPricingType;
  }
): GroupedAddOn[] {
  const groups = new Map<string, GroupedAddOn>();
  items.forEach((item) => {
    const id = getId(item);
    const { name, amount, quantity, pricingType } = getFields(item);
    const existing = groups.get(id);
    if (!existing) {
      groups.set(id, {
        id,
        name,
        amount,
        quantityPerDay: quantity,
        daysCount: 1,
        pricingType,
      });
      return;
    }
    groups.set(id, {
      ...existing,
      amount: existing.amount + amount,
      daysCount: existing.daysCount + 1,
    });
  });
  return Array.from(groups.values());
}

function buildLineFromMatches<T>({
  addOnId,
  matches,
  getName,
  getQuantity,
  getSubTotal,
  fallbackDisplayName,
  pricingType,
}: {
  addOnId: string;
  matches: T[];
  getName: (item: T) => string | undefined;
  getQuantity: (item: T) => number;
  getSubTotal: (item: T) => number;
  fallbackDisplayName?: string;
  pricingType?: AddonPricingType;
}): AddOnsBreakdownLine | undefined {
  if (matches.length === 0) {
    return undefined;
  }
  const displayName =
    getName(matches[0]) || fallbackDisplayName || DEFAULT_ADD_ON_LABEL;
  return {
    id: addOnId,
    label: formatAddOnLabel({
      name: displayName,
      quantityPerDay: getQuantity(matches[0]),
      daysCount: matches.length,
      pricingType,
    }),
    amount: matches.reduce((total, item) => total + getSubTotal(item), 0),
  };
}

// Resolves a line from server data (quote first, then order) — used whenever
// the editable selection can't be computed client-side (capacity add-ons,
// missing offering, or no booking dates yet). Quote items are matched on the
// server-side orderable id (`offering.id`), while order items are matched on
// `details.addOnID` since that's what seeds the editable selection set.
// Returns undefined only if the add-on is nowhere in either data source.
function buildAddOnLineFromServerData({
  addOnId,
  offeringId,
  fallbackDisplayName,
  pricingType,
  quoteItems,
  orderItems,
}: {
  addOnId: string;
  offeringId?: string;
  fallbackDisplayName?: string;
  pricingType?: AddonPricingType;
  quoteItems?: DashboardQuoteItem[];
  orderItems?: OrderItem[];
}): AddOnsBreakdownLine | undefined {
  if (offeringId) {
    const quoteLine = buildLineFromMatches({
      addOnId,
      matches:
        quoteItems?.filter((item) => item.orderableId === offeringId) ?? [],
      getName: (item) => item.details?.addOnName,
      getQuantity: (item) => item.quantity,
      getSubTotal: (item) => item.subTotal,
      fallbackDisplayName,
      pricingType,
    });
    if (quoteLine) {
      return quoteLine;
    }
  }

  return buildLineFromMatches({
    addOnId,
    matches: (orderItems ?? []).filter(
      (item): item is OrderItemMultiPass =>
        isEditableAddOnItem(item) && item.details.addOnID === addOnId
    ),
    getName: (item) => item.details.name,
    getQuantity: (item) => item.quantity,
    getSubTotal: (item) => item.subTotal,
    fallbackDisplayName,
    pricingType,
  });
}

function groupedAddOnsToBreakdown(
  groups: GroupedAddOn[]
): AddOnsBreakdown | undefined {
  return buildBreakdown(
    groups.map((group) => ({
      id: group.id,
      label: formatAddOnLabel({
        name: group.name,
        quantityPerDay: group.quantityPerDay,
        daysCount: group.daysCount,
        pricingType: group.pricingType,
      }),
      amount: group.amount,
    }))
  );
}

// Indexes spaceOfferings by both `id` (quote items reference the server-side
// orderable id) and `addOnID` (order items reference the catalog add-on id)
// so the groupers can resolve pricingType in O(1) per row.
function indexOfferingsByKey(
  spaceOfferings: SpaceOffering[] | undefined,
  getKey: (offering: SpaceOffering) => string
): Map<string, SpaceOffering> {
  const index = new Map<string, SpaceOffering>();
  spaceOfferings?.forEach((offering) => index.set(getKey(offering), offering));
  return index;
}

export function getAddOnsBreakdownFromQuoteItems(
  quoteItems: DashboardQuoteItem[],
  spaceOfferings?: SpaceOffering[]
): AddOnsBreakdown | undefined {
  const offeringsById = indexOfferingsByKey(spaceOfferings, (o) => o.id);
  const addOnItems = quoteItems.filter(
    (item) => item.orderableType === QuoteItemOrderableType.SpaceOffering
  );
  const groups = groupByAddOn(
    addOnItems,
    (item) => item.orderableId,
    (item) => ({
      name: item.details?.addOnName || DEFAULT_ADD_ON_LABEL,
      amount: item.subTotal,
      quantity: item.quantity,
      pricingType: offeringsById.get(item.orderableId)?.pricingType,
    })
  );
  return groupedAddOnsToBreakdown(groups);
}

export function getAddOnsBreakdownFromOrderItems(
  orderItems: OrderItem[],
  spaceOfferings?: SpaceOffering[]
): AddOnsBreakdown | undefined {
  const offeringsByAddOnId = indexOfferingsByKey(
    spaceOfferings,
    (o) => o.addOnID
  );
  const addOnItems = orderItems.filter(
    (item) => item.type === OrderableType.AddOn
  );
  const groups = groupByAddOn(
    addOnItems,
    (item) => item.details.addOnID,
    (item) => ({
      name: item.details.name,
      amount: item.subTotal,
      quantity: item.quantity,
      pricingType: offeringsByAddOnId.get(item.details.addOnID)?.pricingType,
    })
  );
  return groupedAddOnsToBreakdown(groups);
}

// Builds the add-ons price breakdown from the current in-flight selection
// (user-adjusted quantities) so the sidebar can reflect +/- changes before
// the server quote returns. Quantity/Guest add-ons are computed client-side
// when the offering is known and booking dates are set; everything else
// (capacity add-ons, missing offerings, or zero-day state) falls back to
// the last known quote and then to the order items. Access add-ons (e.g.
// multi-pass) aren't part of the editable selection and are always appended
// from the order items.
export function getAddOnsBreakdownFromSelection({
  selections,
  spaceOfferings,
  quoteItems,
  orderItems,
}: {
  // Already resolved to the days each add-on will be submitted for, so this
  // prices exactly what the order will contain.
  selections: AddOnSelections;
  spaceOfferings: SpaceOffering[];
  quoteItems?: DashboardQuoteItem[];
  orderItems?: OrderItem[];
}): AddOnsBreakdown | undefined {
  const lines: AddOnsBreakdownLine[] = [];

  Object.entries(selections).forEach(([addOnId, selection]) => {
    const offering = spaceOfferings.find((item) => item.addOnID === addOnId);
    const daysCount = selection.dates.length;
    // Capacity add-ons need spaceLayoutCapacity to compute totals so we fall
    // back to server data. Flat add-ons can be computed client-side because
    // the offering price is already the per-day whole-space total.
    const canComputeClientSide =
      offering &&
      offering.pricingType !== AddonPricingType.Capacity &&
      daysCount > 0;

    if (canComputeClientSide) {
      const displayName = offering.displayName || offering.name;

      if (isPerBookingPricing(offering.pricingType)) {
        const quantity =
          offering.pricingType === AddonPricingType.QuantityPerBooking
            ? selection.quantity ?? 1
            : 1;
        lines.push({
          id: addOnId,
          label: formatAddOnLabel({
            name: displayName,
            quantityPerDay: quantity,
            daysCount,
            pricingType: offering.pricingType,
          }),
          amount: offering.price * quantity,
        });
        return;
      }

      const quantity =
        offering.pricingType === AddonPricingType.Flat
          ? 1
          : selection.quantity ?? 1;
      lines.push({
        id: addOnId,
        label: formatAddOnLabel({
          name: displayName,
          quantityPerDay: quantity,
          daysCount,
          pricingType: offering.pricingType,
        }),
        amount: offering.price * quantity * daysCount,
      });
      return;
    }

    const fallbackLine = buildAddOnLineFromServerData({
      addOnId,
      offeringId: offering?.id,
      fallbackDisplayName: offering?.displayName || offering?.name,
      pricingType: offering?.pricingType,
      quoteItems,
      orderItems,
    });
    if (fallbackLine) {
      lines.push(fallbackLine);
    }
  });

  orderItems?.forEach((item) => {
    if (!isAccessAddOnItem(item)) {
      return;
    }
    lines.push({
      id: item.details.addOnID,
      label: formatAddOnLabel({
        name: item.details.name,
        quantityPerDay: item.quantity,
        daysCount: 1,
      }),
      amount: item.subTotal,
    });
  });

  return buildBreakdown(lines);
}
